import React from 'react';
import {
    Button,
    Dialog,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    Tooltip,
    makeStyles,
    mergeClasses,
    tokens,
} from '@fluentui/react-components';
import { AddRegular, ArrowUploadRegular, AttachRegular, DismissRegular, FolderRegular, ImageRegular, InfoRegular } from '@fluentui/react-icons';
import { customColors, customFonts } from '../../../styles';
import { alerts } from '../../../shared/ui/Alerts';
import { NexusAgent } from '../../../domain/agents/models/Agent';
import { ChatProviderType } from '../../../domain/chat/models/ChatProviderType';
import useChatInput from '../hooks/useChatInput';

const icon = '& > span > svg';

const useStyles = makeStyles({
    root: {
        position: 'relative',
        display: 'inline-flex',
        flexShrink: 0,
    },
    button: {
        height: '42px',
        width: '42px',
        maxWidth: '42px',
        minWidth: '42px',
        border: '1px solid ' + customColors.protivitiDarkBlue,
        borderRadius: '5px',
        marginRight: '4px',
        transition: 'all 0.3s ease',

        [icon]: {
            color: customColors.protivitiDarkBlue,
            transition: 'all 0.3s ease',
        },

        '&:hover': {
            backgroundColor: customColors.protivitiDarkBlue,

            [icon]: {
                color: customColors.white,
            },
        },

        '&:active': {
            backgroundColor: customColors.protivitiOrange,
            transition: 'all 0s',
        },
    },
    menuList: {
        minWidth: '276px',
        padding: '0px',
        backgroundColor: customColors.white,
        borderRadius: '4px',
        boxShadow: tokens.shadow16,
    },
    menuItem: {
        height: '42px',
        color: customColors.protivitiDarkBlue,
        fontSize: tokens.fontSizeBase400,
        borderRadius: '4px',
        alignItems: 'center',
        padding: '0px 10px',
        ':hover': {
            outlineStyle: 'solid',
            outlineWidth: '2px',
            outlineColor: customColors.black,
            backgroundColor: customColors.white,
        },
    },
    menuItemDisabled: {
        color: tokens.colorNeutralForegroundDisabled,
        cursor: 'not-allowed',
        opacity: 0.6,
        ':hover': {
            outlineStyle: 'none',
        },
    },
    menuItemContent: {
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: tokens.spacingHorizontalM,
    },
    menuItemLabel: {
        display: 'inline-flex',
        alignItems: 'center',
        minWidth: 0,
        gap: tokens.spacingHorizontalS,
        '& svg': {
            color: customColors.protivitiOrange,
            flexShrink: 0,
        },
    },
    menuItemText: {
        whiteSpace: 'nowrap',
    },
    infoButton: {
        minWidth: '24px',
        width: '24px',
        height: '24px',
        color: customColors.protivitiDarkBlue,
        padding: '0px',
        ':hover': {
            color: customColors.protivitiOrange,
        },
    },
    hiddenInput: {
        display: 'none',
    },
    dialogSurface: {
        width: '748px',
        maxWidth: 'calc(100vw - 32px)',
        minHeight: '700px',
        padding: '44px 30px 52px',
        borderRadius: '6px',
    },
    dialogBody: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        rowGap: '28px',
    },
    dialogTitle: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        color: customColors.protivitiDarkBlue,
        fontFamily: customFonts.Lato,
        fontSize: '24px',
        fontWeight: 700,
        lineHeight: '32px',
        textAlign: 'center',
    },
    closeButton: {
        position: 'absolute',
        top: '-10px',
        right: '0',
        minWidth: '36px',
        width: '36px',
        height: '36px',
        color: customColors.protivitiDarkBlue,
        padding: '0px',
    },
    dialogText: {
        color: '#9a9a9a',
        fontFamily: customFonts.Lato,
        fontSize: '18px',
        lineHeight: '32px',
        textAlign: 'center',
        margin: '0px',
    },
    dropZone: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '220px',
        color: '#2c2c2c',
        backgroundColor: customColors.white,
        border: '2px dashed #666666',
        cursor: 'pointer',
    },
    dropZoneActive: {
        backgroundColor: '#f7fbff',
        border: '2px dashed ' + customColors.protivitiOrange,
    },
    uploadIcon: {
        color: '#52697c',
        fontSize: '30px',
        marginBottom: '18px',
    },
    dropPrimaryText: {
        fontFamily: customFonts.Lato,
        fontSize: '18px',
        fontWeight: 700,
        lineHeight: '24px',
    },
    dropSecondaryText: {
        color: '#5f5f5f',
        fontFamily: customFonts.Lato,
        fontSize: '16px',
        lineHeight: '24px',
    },
    templateLink: {
        color: customColors.protivitiOrange,
        textDecorationLine: 'underline',
    },
});

interface UploadMenuProps {
    isBulkPromptProcessing?: boolean;
    isDocumentUploading?: boolean;
    resolvedSessionAgent?: NexusAgent | null;
    onBulkPromptUpload?: (files: FileList) => void;
    onDocumentUpload?: (files: FileList) => void;
    onImageUpload?: (file: File) => void;
}

const MAX_FILE_COUNT_PROGPT = 100;
const MAX_FILE_COUNT_NEXUS = 15;
const MAX_FILE_SIZE_MB_PROGPT = 30;
const MAX_FILE_SIZE_MB_NEXUS = 20;

const fileSearchAllowedExtensions = ['doc', 'docx', 'ppt', 'pptx', 'txt', 'pdf', 'png', 'jpeg', 'jpg'];
const codeInterpreterAllowedExtensions = [
    'csv', 'xlsx', 'docx', 'html', 'java', 'json', 'md', 'pdf', 'php', 'pptx', 'py', 'rb',
    'tex', 'txt', 'css', 'jpeg', 'jpg', 'gif', 'png', 'tar', 'ts', 'xml', 'zip', 'c',
    'cpp', 'cs', 'doc', 'js', 'sh',
];

const UploadMenu: React.FC<UploadMenuProps> = ({
    isBulkPromptProcessing = false,
    isDocumentUploading = false,
    resolvedSessionAgent = null,
    onBulkPromptUpload,
    onDocumentUpload,
    onImageUpload,
}) => {
    const styles = useStyles();
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);
    const bulkPromptInputRef = React.useRef<HTMLInputElement>(null);
    const documentInputRef = React.useRef<HTMLInputElement>(null);
    const imageInputRef = React.useRef<HTMLInputElement>(null);
    const [isBulkPromptDialogOpen, setIsBulkPromptDialogOpen] = React.useState(false);
    const [isDocumentDialogOpen, setIsDocumentDialogOpen] = React.useState(false);
    const [isDragActive, setIsDragActive] = React.useState(false);
    const [dialogType, setDialogType] = React.useState<'bulk' | 'document'>('bulk');
    const chatInputVM = useChatInput();
    const isNexusAgent = resolvedSessionAgent != null && resolvedSessionAgent.providerType === ChatProviderType.Nexus;
    const acceptedFileTypes = (isNexusAgent && resolvedSessionAgent.allowCodeInterpreterAtConversationLevel)
        ? codeInterpreterAllowedExtensions.map((ext) => `.${ext}`).join(',')
        : fileSearchAllowedExtensions.map((ext) => `.${ext}`).join(',');
    
    const MAX_FILE_COUNT = isNexusAgent ? MAX_FILE_COUNT_NEXUS : MAX_FILE_COUNT_PROGPT;
    const MAX_FILE_SIZE_MB = isNexusAgent ? MAX_FILE_SIZE_MB_NEXUS : MAX_FILE_SIZE_MB_PROGPT;

    const handleFileInputChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        onUpload: ((files: FileList) => void) | undefined,
    ) => {
        if (event.target.files && event.target.files.length > 0) {
            onUpload?.(event.target.files);
            setIsMenuOpen(false);
        }

        event.target.value = '';
    };

    const handleBulkPromptInfoClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setDialogType('bulk');
        setIsBulkPromptDialogOpen(true);
    };

    const handleBulkPromptDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragActive(false);

        if (event.dataTransfer.files.length > 0) {
            onBulkPromptUpload?.(event.dataTransfer.files);
            setIsBulkPromptDialogOpen(false);
            setIsMenuOpen(false);
        }
    };

    const handleDocumentInfoClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setDialogType('document');
        setIsDocumentDialogOpen(true);
    };

    const handleDocumentDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragActive(false);

        if (event.dataTransfer.files.length > 0) {
            onDocumentUpload?.(event.dataTransfer.files);
            setIsDocumentDialogOpen(false);
        }
    };

    const templateLink = process.env.REACT_APP_BULK_PROMPT_TEMPLATE_LINK;

    return (
        <div className={styles.root}>
            <Menu
                open={isMenuOpen}
                onOpenChange={(_event, data) => setIsMenuOpen(data.open)}
                positioning={{ position: 'above', offset: { mainAxis: 10 } }}
            >
                <MenuTrigger disableButtonEnhancement>
                    <Button className={styles.button} appearance="transparent" icon={<AddRegular />} aria-label="Upload files" />
                </MenuTrigger>

                <MenuPopover>
                    <MenuList className={styles.menuList}>
                        {chatInputVM?.capabilities?.supportsBulkPromptUpload &&
                            <MenuItem
                                className={mergeClasses(styles.menuItem, isBulkPromptProcessing && styles.menuItemDisabled)}
                                disabled={isBulkPromptProcessing}
                                onClick={() => bulkPromptInputRef.current?.click()}
                            >
                                <div className={styles.menuItemContent}>
                                    <span className={styles.menuItemLabel}>
                                        <FolderRegular />
                                        <span className={styles.menuItemText}>Upload Bulk Prompts</span>
                                    </span>
                                    <Button
                                        appearance="transparent"
                                        className={styles.infoButton}
                                        icon={<InfoRegular />}
                                        aria-label="Bulk prompt upload info"
                                        onClick={handleBulkPromptInfoClick}
                                    />
                                </div>
                            </MenuItem>
                        }
                        {chatInputVM?.capabilities?.supportsDocumentUpload &&
                            <MenuItem
                                className={mergeClasses(styles.menuItem, isDocumentUploading && styles.menuItemDisabled)}
                                disabled={isDocumentUploading}
                                onClick={() => documentInputRef.current?.click()}
                            >
                                <div className={styles.menuItemContent}>
                                    <span className={styles.menuItemLabel}>
                                        <AttachRegular />
                                        <span className={styles.menuItemText}>Upload Documents</span>
                                    </span>
                                    <Button
                                        appearance="transparent"
                                        className={styles.infoButton}
                                        icon={<InfoRegular />}
                                        aria-label="Document upload info"
                                        onClick={handleDocumentInfoClick}
                                    />
                                </div>
                            </MenuItem>
                        }
                        {onImageUpload && (
                            <MenuItem
                                className={styles.menuItem}
                                onClick={() => imageInputRef.current?.click()}
                            >
                                <div className={styles.menuItemContent}>
                                    <span className={styles.menuItemLabel}>
                                        <ImageRegular />
                                        <span className={styles.menuItemText}>Upload Images</span>
                                    </span>
                                    <Tooltip
                                        content="Up to 3 images per message · Max 2 MB each · JPEG, PNG, GIF, WebP"
                                        relationship="description"
                                        positioning="above-end"
                                    >
                                        <Button
                                            appearance="transparent"
                                            className={styles.infoButton}
                                            icon={<InfoRegular />}
                                            aria-label="Image upload info"
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </Tooltip>
                                </div>
                            </MenuItem>
                        )}
                    </MenuList>
                </MenuPopover>
            </Menu>

            <input
                ref={bulkPromptInputRef}
                className={styles.hiddenInput}
                type="file"
                accept=".xlsx"
                aria-label="Upload bulk prompt file"
                onChange={(event) => handleFileInputChange(event, onBulkPromptUpload)}
            />
            <input
                ref={documentInputRef}
                className={styles.hiddenInput}
                type="file"
                accept={acceptedFileTypes}
                multiple
                aria-label="Upload documents"
                onChange={(event) => handleFileInputChange(event, onDocumentUpload)}
            />
            <input
                ref={imageInputRef}
                className={styles.hiddenInput}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                multiple
                aria-label="Upload images"
                onChange={(event) => {
                    if (event.target.files && event.target.files.length > 0) {
                        const MAX_IMAGE_COUNT = 3;
                        const remaining = MAX_IMAGE_COUNT - (chatInputVM.pendingImages?.length ?? 0);
                        const files = Array.from(event.target.files);
                        if (remaining <= 0) {
                            alerts.warning(`You can attach up to ${MAX_IMAGE_COUNT} images per message.`);
                        } else {
                            if (files.length > remaining) {
                                alerts.warning(`You can attach up to ${MAX_IMAGE_COUNT} images per message. Only ${remaining} image(s) were added.`);
                            }
                            files.slice(0, remaining).forEach(file => onImageUpload?.(file));
                        }
                    }
                    event.target.value = '';
                    setIsMenuOpen(false);
                }}
            />

            <Dialog open={isBulkPromptDialogOpen} onOpenChange={(_event, data) => setIsBulkPromptDialogOpen(data.open)}>
                <DialogSurface className={styles.dialogSurface}>
                    <DialogBody className={styles.dialogBody}>
                        <DialogTitle className={styles.dialogTitle}>
                            Upload Your Bulk Prompt File
                            <Button
                                appearance="transparent"
                                className={styles.closeButton}
                                icon={<DismissRegular />}
                                aria-label="Close bulk prompt upload information"
                                onClick={() => setIsBulkPromptDialogOpen(false)}
                            />
                        </DialogTitle>
                        <DialogContent>
                            <p className={styles.dialogText}>
                                The Bulk Upload feature enables you to upload an Excel file with up to 50 prompts,
                                automating interactions with your agent. Once processed, responses are saved in your
                                chat history, and a downloadable Excel file is provided. During processing, this chat
                                session is temporarily unavailable, but other sessions remain accessible.
                            </p>
                        </DialogContent>
                        <div
                            className={mergeClasses(styles.dropZone, isDragActive && dialogType === 'bulk' && styles.dropZoneActive)}
                            role="button"
                            tabIndex={0}
                            onClick={() => bulkPromptInputRef.current?.click()}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    bulkPromptInputRef.current?.click();
                                }
                            }}
                            onDragEnter={(event) => {
                                event.preventDefault();
                                setDialogType('bulk');
                                setIsDragActive(true);
                            }}
                            onDragOver={(event) => {
                                event.preventDefault();
                                setIsDragActive(true);
                            }}
                            onDragLeave={(event) => {
                                event.preventDefault();
                                setIsDragActive(false);
                            }}
                            onDrop={handleBulkPromptDrop}
                        >
                            <ArrowUploadRegular className={styles.uploadIcon} />
                            <span className={styles.dropPrimaryText}>Drag &amp; Drop files here</span>
                            <span className={styles.dropSecondaryText}>or click to select files</span>
                        </div>
                        <p className={styles.dialogText}>
                            Excel files only. Files must be formatted with 2 columns, the first titled "Prompts" with
                            your rows of prompts listed below, and the second titled "Response" with empty rows below.
                            See the template for this format{' '}
                            {templateLink ? (
                                <a
                                    className={styles.templateLink}
                                    href={templateLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    here.
                                </a>
                            ) : (
                                <span className={styles.templateLink}>here.</span>
                            )}
                        </p>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
            
            <Dialog open={isDocumentDialogOpen} onOpenChange={(_event, data) => setIsDocumentDialogOpen(data.open)}>
                <DialogSurface className={styles.dialogSurface}>
                    <DialogBody className={styles.dialogBody}>
                        <DialogTitle className={styles.dialogTitle}>
                            Document Upload
                            <Button
                                appearance="transparent"
                                className={styles.closeButton}
                                icon={<DismissRegular />}
                                aria-label="Close document upload information"
                                onClick={() => setIsDocumentDialogOpen(false)}
                            />
                        </DialogTitle>
                        <DialogContent>
                            <p className={styles.dialogText}>
                                {isNexusAgent
                                    ? `You can upload up to ${MAX_FILE_COUNT} documents. The maximum size for each document is ${MAX_FILE_SIZE_MB} MB.`
                                    : `You can upload up to ${MAX_FILE_COUNT} documents. The maximum size for each document is ${MAX_FILE_SIZE_MB} MB. Supported file types are Microsoft Word, PowerPoint, Portable Document Format (PDF), Text (TXT), JPG, JPEG, and PNG.`}
                            </p>
                        </DialogContent>
                        <div
                            className={mergeClasses(styles.dropZone, isDragActive && dialogType === 'document' && styles.dropZoneActive)}
                            role="button"
                            tabIndex={0}
                            onClick={() => documentInputRef.current?.click()}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    documentInputRef.current?.click();
                                }
                            }}
                            onDragEnter={(event) => {
                                event.preventDefault();
                                setDialogType('document');
                                setIsDragActive(true);
                            }}
                            onDragOver={(event) => {
                                event.preventDefault();
                                setIsDragActive(true);
                            }}
                            onDragLeave={(event) => {
                                event.preventDefault();
                                setIsDragActive(false);
                            }}
                            onDrop={handleDocumentDrop}
                        >
                            <ArrowUploadRegular className={styles.uploadIcon} />
                            <span className={styles.dropPrimaryText}>Drag &amp; Drop files here</span>
                            <span className={styles.dropSecondaryText}>or click to select files</span>
                        </div>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </div>
    );
};

export default UploadMenu;